import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Clock, LucideIcon } from 'lucide-react';
import { ProBadge } from './SubscriptionFeatures';

/** The two top-level sections of the site, shared by both navs below. */
const SECTIONS: {
  to: string;
  icon: LucideIcon;
  text: string;
  isPro?: boolean;
  /** Extra paths that live under this section, so it stays highlighted. */
  matches?: (pathname: string) => boolean;
}[] = [
  {
    to: '/',
    icon: Home,
    text: 'Tracker',
    // A city's venue list is the tracker, just drilled into.
    matches: (p) => p.startsWith('/venues'),
  },
  { to: '/availability', icon: Clock, text: 'Availability', isPro: true },
];

const isSectionActive = (
  section: (typeof SECTIONS)[number],
  pathname: string
): boolean => pathname === section.to || !!section.matches?.(pathname);

/** Inline links beside the logo. Desktop only - see MobileHeaderNav. */
const HeaderNav: React.FC = () => {
  const location = useLocation();

  return (
    <div className="hidden md:flex items-center space-x-2">
      {SECTIONS.map((section) => {
        const isActive = isSectionActive(section, location.pathname);

        return (
          <Link
            key={section.to}
            to={section.to}
            aria-current={isActive ? 'page' : undefined}
            className={`flex items-center px-3 py-2 rounded-md transition-colors duration-200
              ${isActive
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}
            `}
          >
            <span>{section.text}</span>
            {section.isPro && <ProBadge className="ml-2" />}
          </Link>
        );
      })}
    </div>
  );
};

/**
 * Mobile nav: a segmented control sitting directly under the logo.
 *
 * These two sections used to live behind a hamburger, which hid the whole
 * point of the site behind a tap nobody made. There is no room for them
 * alongside the logo and the sign-in button on a phone, so they get their own
 * full-width row instead.
 */
export const MobileHeaderNav: React.FC = () => {
  const location = useLocation();

  return (
    <nav
      aria-label="Sections"
      className="md:hidden mb-3 flex items-stretch gap-1 rounded-xl
                 bg-gray-100/80 p-1 ring-1 ring-inset ring-gray-200/70"
    >
      {SECTIONS.map((section) => {
        const { to, icon: Icon, text, isPro } = section;
        const isActive = isSectionActive(section, location.pathname);

        return (
          <Link
            key={to}
            to={to}
            aria-current={isActive ? 'page' : undefined}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2.5 py-2
              text-[13px] font-semibold whitespace-nowrap
              transition-all duration-200
              ${isActive
                ? 'bg-white text-blue-600 shadow-sm ring-1 ring-gray-200/80'
                : 'text-gray-600 active:bg-white/70'}
            `}
          >
            <Icon
              strokeWidth={2}
              className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-blue-500' : 'text-gray-400'}`}
            />
            <span>{text}</span>
            {/* Hidden on the narrowest phones, where the label needs the room. */}
            {isPro && <ProBadge size="sm" hideIcon className="hidden min-[375px]:inline-flex" />}
          </Link>
        );
      })}
    </nav>
  );
};

export default HeaderNav;
